const PLACEHOLDER_PATTERN = /veuillez\s+s[ée]lectionner/i;

class MaxProductOptions extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.eventSelect = this.querySelector('[data-max-event]');
    this.locationSelect = this.querySelector('[data-max-location]');
    this.integralCheckbox = this.querySelector('[data-max-integral]');
    this.duoCheckbox = this.querySelector('[data-max-duo]');
    this.optionsProperty = this.querySelector('[data-max-options-property]');
    this.dayTotalProperty = this.querySelector('[data-max-day-total-property]');
    this.basePrice = this.querySelector('[data-max-base-price]');
    this.error = this.querySelector('[data-max-error]');
    this.datetimeInput = this.querySelector('[data-max-datetime]');
    this.form = document.getElementById(this.dataset.formId || '');
    this.variants = this.parseVariants();

    if (this.form) {
      this.form.noValidate = true;
      this.form.classList.add('max-product-form');
    }
    document.documentElement.classList.add('max-options-active');

    const heading = this.closest('.product-details')?.querySelector('h1');
    this.headingContainer = heading?.parentElement || null;
    if (this.headingContainer && this.basePrice) {
      this.headingContainer.classList.add('max-product-heading-price');
      this.headingContainer.append(this.basePrice);
    }

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleAddClick = this.handleAddClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDatePickerClick = this.handleDatePickerClick.bind(this);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('keydown', this.handleKeydown, true);
    this.datetimeInput?.addEventListener('click', this.handleDatePickerClick);
    this.form?.addEventListener('submit', this.handleSubmit, true);
    this.form?.addEventListener('click', this.handleAddClick, true);
    this.depoObserver = new MutationObserver(() => this.updateDayTotalAvailability());
    this.depoObserver.observe(document.body, { childList: true, subtree: true });

    if (new URL(window.location.href).searchParams.has('variant')) {
      this.restoreExplicitVariant();
    }
    this.updateOptionProperty();
    this.updateDayTotalProperty();
    this.updateDayTotalAvailability();
    this.updateBasePrice();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('keydown', this.handleKeydown, true);
    this.datetimeInput?.removeEventListener('click', this.handleDatePickerClick);
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    this.form?.removeEventListener('click', this.handleAddClick, true);
    this.depoObserver?.disconnect();
    this.form?.classList.remove('max-product-form');
    this.headingContainer?.classList.remove('max-product-heading-price');
    this.basePrice?.remove();
    document.documentElement.classList.remove('max-options-active');
  }

  parseVariants() {
    try {
      return JSON.parse(this.querySelector('[data-max-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Impossible de lire les variantes du produit Max.', error);
      return [];
    }
  }

  get extraValue() {
    const integral = Boolean(this.integralCheckbox?.checked);
    const duo = Boolean(this.duoCheckbox?.checked);
    if (integral && duo) return 'Striptease intégral + duo';
    if (integral) return 'Striptease intégral';
    if (duo) return 'Striptease en duo';
    return 'Aucune';
  }

  get selection() {
    return [this.eventSelect?.value || '', this.locationSelect?.value || '', this.extraValue];
  }

  get selectedVariant() {
    const selection = this.selection;
    if (!selection[0] || !selection[1]) return null;
    return this.variants.find((variant) =>
      variant.available !== false && variant.options.every((option, index) => option === selection[index])
    ) || null;
  }

  get displayedPrice() {
    const eventValue = this.eventSelect?.value || '';
    const locationValue = this.locationSelect?.value || '';
    const extraValue = this.extraValue;
    const matchingPrices = this.variants
      .filter((variant) =>
        variant.available !== false &&
        (!eventValue || variant.options[0] === eventValue) &&
        (!locationValue || variant.options[1] === locationValue) &&
        variant.options[2] === extraValue
      )
      .map((variant) => Number(variant.price))
      .filter(Number.isFinite);

    if (matchingPrices.length) return Math.min(...matchingPrices);

    let fallbackPrice = Number(this.dataset.basePrice || 0);
    if (this.integralCheckbox?.checked) fallbackPrice += 5000;
    if (this.duoCheckbox?.checked) fallbackPrice += 15000;
    return fallbackPrice;
  }

  handleDatePickerClick() {
    if (!(this.datetimeInput instanceof HTMLInputElement)) return;
    this.datetimeInput.focus();
    try {
      if (typeof this.datetimeInput.showPicker === 'function') {
        this.datetimeInput.showPicker();
      }
    } catch (error) {
      // Le focus garde le contrôle utilisable lorsque le navigateur bloque l’ouverture programmatique.
    }
  }

  handleChange(event) {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    this.clearError(event.target);
    this.updateOptionProperty();
    this.updateDayTotalProperty();
    this.updateDayTotalAvailability();
    this.updateBasePrice();
    this.syncNativeVariant();
  }

  updateOptionProperty() {
    if (this.optionsProperty) this.optionsProperty.value = this.extraValue;
  }

  updateDayTotalProperty() {
    if (!this.dayTotalProperty) return;
    const currency = this.dataset.currency || 'EUR';
    const totalPrice = Number(this.selectedVariant?.price ?? this.displayedPrice);
    if (!Number.isFinite(totalPrice)) return;
    this.dayTotalProperty.value = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(totalPrice / 100);
  }

  updateDayTotalAvailability() {
    if (!this.dayTotalProperty) return;
    const depoPartPayment = document.querySelector(
      '[name="properties[_part_payment_id]"]:not([value="full"]), [name="_part_payment_id"]:not([value="full"])'
    );
    this.dayTotalProperty.disabled = !depoPartPayment;
  }

  updateBasePrice() {
    if (!this.basePrice) return;
    const currency = this.dataset.currency || 'EUR';
    const displayedPrice = this.displayedPrice;
    this.basePrice.textContent = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(displayedPrice / 100);
  }

  syncNativeVariant() {
    const variant = this.selectedVariant;
    if (!variant) return;

    const picker = this.parentElement?.querySelector('.max-product-options__native variant-picker');
    if (!picker) return;

    const selection = this.selection;
    let trigger = null;
    const selects = Array.from(picker.querySelectorAll('select'));

    if (selects.length === selection.length) {
      selects.forEach((select, index) => {
        select.value = selection[index];
        picker.updateSelectedOption?.(select);
      });
      trigger = selects.at(-1)?.selectedOptions[0] || null;
    } else {
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      fieldsets.forEach((fieldset, index) => {
        const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        inputs.forEach((input) => {
          const isSelected = input.value === selection[index];
          input.checked = isSelected;
          input.dataset.currentChecked = isSelected ? 'true' : 'false';
          if (isSelected) trigger = input;
        });
      });
    }

    if (!(trigger instanceof HTMLElement)) return;
    const requestUrl = picker.buildRequestUrl?.(trigger);
    if (!requestUrl || typeof picker.fetchUpdatedSection !== 'function') return;

    picker.fetchUpdatedSection(requestUrl, {
      detail: {
        optionValueId: trigger.dataset.optionValueId || '',
        variantId: String(variant.id),
        connectedProductUrl: trigger.dataset.connectedProductUrl || '',
      },
    });

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url.toString());
  }

  restoreExplicitVariant() {
    const variantId = this.dataset.initialVariantId;
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    if (this.eventSelect) this.eventSelect.value = variant.options[0] || '';
    if (this.locationSelect) this.locationSelect.value = variant.options[1] || '';
    if (this.integralCheckbox) this.integralCheckbox.checked = /intégral/.test(variant.options[2] || '');
    if (this.duoCheckbox) this.duoCheckbox.checked = /duo/.test(variant.options[2] || '');
  }

  handleAddClick(event) {
    const submitter = event.target instanceof Element
      ? event.target.closest('button[type="submit"], input[type="submit"]')
      : null;
    if (!submitter || !this.form?.contains(submitter)) return;
    this.updateDayTotalProperty();
    this.updateDayTotalAvailability();
    this.validateOrBlock(event);
  }

  handleKeydown(event) {
    if (event.key !== 'Enter' || event.target instanceof HTMLTextAreaElement) return;
    this.validateOrBlock(event);
  }

  handleSubmit(event) {
    if (event.target !== this.form) return;
    this.updateDayTotalProperty();
    this.updateDayTotalAvailability();
    this.validateOrBlock(event);
  }

  validateOrBlock(event) {
    const invalidSelect = [this.eventSelect, this.locationSelect].find((select) => !select?.value);
    const invalidExternalField = Array.from(this.querySelectorAll(`[form="${CSS.escape(this.dataset.formId || '')}"]`))
      .find((field) => typeof field.checkValidity === 'function' && !field.checkValidity());

    if (!invalidSelect && !invalidExternalField) return true;

    event.preventDefault();
    event.stopImmediatePropagation();
    const field = invalidSelect || invalidExternalField;
    this.showError(field, invalidSelect
      ? 'Veuillez sélectionner le type et le lieu de l’évènement avant d’ajouter au panier.'
      : 'Veuillez renseigner tous les champs obligatoires avant d’ajouter au panier.');
    field?.focus();
    return false;
  }

  showError(field, message) {
    if (field instanceof HTMLElement) field.setAttribute('aria-invalid', 'true');
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError(field) {
    if (field instanceof HTMLElement) field.removeAttribute('aria-invalid');
    if (!this.error) return;
    const stillInvalid = [this.eventSelect, this.locationSelect].some((select) => !select?.value);
    if (!stillInvalid) {
      this.error.hidden = true;
      this.error.textContent = '';
    }
  }
}

if (!customElements.get('max-product-options')) {
  customElements.define('max-product-options', MaxProductOptions);
}

// Filet de sécurité pour les autres sélecteurs personnalisés du thème ou d’une application.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
  const invalid = Array.from(form.querySelectorAll('select')).find((select) => {
    const option = select.selectedOptions[0];
    return !select.value && PLACEHOLDER_PATTERN.test(option?.textContent || '');
  });
  if (!invalid) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  invalid.setAttribute('aria-invalid', 'true');
  invalid.focus();
}, true);
